import { Category, Media } from '@gripp/shared-logic';
import { CategoryBadge } from 'libs/shared-ui/src/components/media/categoryBadge';
import { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';

type MediaExtended = Media & { isFromProduct?: boolean };

interface CategoryFilterProps {
  allFiles: MediaExtended[];
  selectedCategoryId: string | undefined;
  onCategorySelect: (categoryId: string | undefined) => void;
}

export const CategoryFilter = ({
  allFiles,
  selectedCategoryId,
  onCategorySelect,
}: CategoryFilterProps) => {
  // Extract unique categories and their counts
  const categories = useMemo(() => {
    const uniqueCategories = new Map<
      string,
      { category: Category; count: number }
    >();

    allFiles.forEach((file) => {
      file.categories?.forEach((category) => {
        if (category) {
          const existing = uniqueCategories.get(category.id);

          if (existing) {
            uniqueCategories.set(category.id, {
              category: category,
              count: existing.count + 1,
            });
          } else {
            uniqueCategories.set(category.id, {
              category: category,
              count: 1,
            });
          }
        }
      });
    });

    return Array.from(uniqueCategories.entries()).map(([id, data]) => ({
      category: data.category,
      count: data.count,
    }));
  }, [allFiles]);

  const handleCategorySelect = (categoryId: string) => {
    if (categoryId === 'all') {
      onCategorySelect(undefined);
    } else {
      onCategorySelect(categoryId);
    }
  };

  if (categories.length === 0) {
    return null; // No categories to display
  }

  return (
    <View style={styles.categoryContainer}>
      <CategoryBadge
        category={{ id: 'all', name: 'All' }}
        count={allFiles.length}
        onCategorySelect={() => handleCategorySelect('all')}
        isSelected={!selectedCategoryId}
      />

      {categories.map(({ category, count }) => (
        <CategoryBadge
          key={category.id}
          category={category}
          count={count}
          onCategorySelect={handleCategorySelect}
          isSelected={selectedCategoryId === category.id}
        />
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  categoryContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 5,
    marginBottom: 16,
  },
});
